/**
 * Redimensiona mantendo o aspecto original (SEM quadrar), reduzindo só se o maior lado
 * passar de maxLado (nunca amplia). Saída JPEG com fundo branco — segura para o backend
 * ler e converter em WebP. Usada na Galeria (upload de fotos avulsas).
 */
export const redimensionarMantendoAspecto = (
    file: File,
    maxLado = 1280,
    quality = 0.85
) => {
    return new Promise<File>((resolve, reject) => {
        if (!file?.type?.startsWith("image/")) {
            return reject(new Error("O arquivo não é uma imagem."))
        }

        const img = new Image()
        const url = URL.createObjectURL(file)

        img.onload = () => {
            const escala = Math.min(1, maxLado / Math.max(img.width, img.height))
            const newW = Math.max(1, Math.round(img.width * escala))
            const newH = Math.max(1, Math.round(img.height * escala))

            const canvas = document.createElement("canvas")
            canvas.width = newW
            canvas.height = newH

            const ctx = canvas.getContext("2d")
            if (!ctx) {
                URL.revokeObjectURL(url)
                return reject(new Error("Falha ao obter contexto do canvas."))
            }

            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = "high"
            ctx.fillStyle = "#ffffff"
            ctx.fillRect(0, 0, newW, newH)
            ctx.drawImage(img, 0, 0, newW, newH)

            canvas.toBlob(
                blob => {
                    URL.revokeObjectURL(url)
                    if (!blob) {
                        return reject(new Error("Falha ao processar a imagem."))
                    }
                    const baseName = file.name.replace(/\.\w+$/, "")
                    const resizedFile = new File([blob], `${baseName}.jpg`, {
                        type: "image/jpeg",
                        lastModified: Date.now(),
                    })
                    resolve(resizedFile)
                },
                "image/jpeg",
                quality
            )
        }

        img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error("Não foi possível carregar a imagem."))
        }

        img.src = url
    })
}

/**
 * Foto de produto/opção do cardápio: mantém o aspecto (SEM quadrar) e reduz só se o maior
 * lado passar de 1080px (nunca amplia) — mesmo teto do backend
 * (UploadAmazonUtil.TETO_RESOLUCAO_PRODUTO_PADRAO). 1080 deixa a foto nítida no zoom da
 * página do produto no site. Saída WebP preservando transparência; navegador sem suporte a
 * WebP no canvas (Safari antigo) gera PNG e o arquivo vai com a extensão real.
 */
export const LADO_MAXIMO_FOTO_PRODUTO = 1080

export const redimensionarImagemProduto = (file: File) => {
    const quality = 0.85
    const mimeType = "image/webp"

    return new Promise<File>((resolve, reject) => {
        if (!file?.type?.startsWith("image/")) {
            return reject(new Error("O arquivo não é uma imagem."))
        }

        const img = new Image()
        const url = URL.createObjectURL(file)

        img.onload = () => {
            const escala = Math.min(1, LADO_MAXIMO_FOTO_PRODUTO / Math.max(img.width, img.height))
            const newW = Math.max(1, Math.round(img.width * escala))
            const newH = Math.max(1, Math.round(img.height * escala))

            const canvas = document.createElement("canvas")
            canvas.width = newW
            canvas.height = newH

            const ctx = canvas.getContext("2d")
            if (!ctx) {
                URL.revokeObjectURL(url)
                return reject(new Error("Falha ao obter contexto do canvas."))
            }

            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = "high"
            ctx.clearRect(0, 0, newW, newH)
            ctx.drawImage(img, 0, 0, newW, newH)

            canvas.toBlob(
                blob => {
                    URL.revokeObjectURL(url)

                    if (!blob) {
                        return reject(new Error("Falha ao processar a imagem."))
                    }

                    const ext = blob.type === "image/webp" ? "webp" : "png"
                    const baseName = file.name.replace(/\.\w+$/, "")

                    resolve(
                        new File([blob], `${baseName}.${ext}`, {
                            type: blob.type || mimeType,
                            lastModified: Date.now(),
                        })
                    )
                },
                mimeType,
                quality
            )
        }

        img.onerror = () => {
            URL.revokeObjectURL(url)
            reject(new Error("Não foi possível carregar a imagem."))
        }

        img.src = url
    })
}
